import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ShieldCheck,
  Layers,
  Users,
  KeyRound,
  Building2,
  AppWindow,
  CreditCard,
  Building,
  ChevronRight,
  LogOut,
} from "lucide-react";
import Swal from "sweetalert2";
import axios from "axios";
import { MAIN_URL } from "../../Configurations/Urls";
import { CORE_FRONTEND_PERMISSIONS_DISABLED } from "../../Configurations/corePermissionsFlags";
import { coreHasPermission } from "../../Configurations/corePermissionUtils";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import {
  clearLocalAuthSession,
  isLocalAuthToken,
} from "../../Configurations/coreLocalAuth";

const SECTION_BEFORE = {
  Dashboard: "Overview",
  "Application Module Features": "Configuration",
  Clients: "Clients",
  "Client Payments": "Operations",
};

function NavChildren({ open, children }) {
  const ref = useRef(null);
  const [h, setH] = useState(open ? "auto" : 0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open) {
      const target = el.scrollHeight;
      setH(target);
      const t = setTimeout(() => setH("auto"), 230);
      return () => clearTimeout(t);
    } else {
      setH(el.scrollHeight);
      requestAnimationFrame(() => requestAnimationFrame(() => setH(0)));
    }
  }, [open]);
  return (
    <div
      className="ems-nav-children"
      ref={ref}
      style={{ height: h === "auto" ? "auto" : h + "px" }}
    >
      <div style={{ paddingBottom: 4 }}>{children}</div>
    </div>
  );
}

export default function SideBar({ Permission, collapsed, onRequestExpand }) {
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const sidebarOptions = useMemo(() => [
    {
      heading: "Dashboard",
      route: "/",
      title: "Dashboard",
      icon: <LayoutDashboard size={19} strokeWidth={2} />,
      PermissionKey: "",
    },
    {
      heading: "Application Module Features",
      route: "/permissions/access",
      title: "Application Module Features",
      icon: <ShieldCheck size={19} strokeWidth={2} />,
      PermissionKey: "",
    },
    {
      heading: "Application Editions",
      route: "/editions/access",
      title: "Application Editions",
      icon: <Layers size={19} strokeWidth={2} />,
      PermissionKey: "",
    },
    {
      heading: "Application User Roles",
      route: "/user-roles/access",
      title: "Application User Roles",
      icon: <Users size={19} strokeWidth={2} />,
      PermissionKey: "",
    },
    {
      heading: "Application User Role Permissions",
      route: "/role-permissions/access",
      title: "Application User Role Permissions",
      icon: <KeyRound size={19} strokeWidth={2} />,
      PermissionKey: "",
    },
    {
      heading: "Clients",
      route: "/organization/client",
      title: "Clients",
      icon: <Building2 size={19} strokeWidth={2} />,
      PermissionKey: "USER_VIEW_ALL",
    },
    {
      heading: "Assign App to Client",
      route: "/organization/client/assign-client-application",
      title: "Assign App to Client",
      icon: <AppWindow size={19} strokeWidth={2} />,
      PermissionKey: "USER_VIEW_ALL",
    },
    {
      heading: "Assign App to Organization",
      route: "/organization/client/assign-organization-application",
      title: "Assign App to Organization",
      icon: <AppWindow size={19} strokeWidth={2} />,
      PermissionKey: "USER_VIEW_ALL",
    },
    {
      heading: "Client Payments",
      title: "Client Payments",
      icon: <CreditCard size={19} strokeWidth={2} />,
      children: [
        {
          heading: "Client Payments",
          route: "/organization/client-payments",
          title: "Client Payments",
          icon: <CreditCard size={16} strokeWidth={2} />,
          PermissionKey: "USER_VIEW_ALL",
        },
      ],
    },
    {
      heading: "Client Organizations",
      title: "Client Organizations",
      icon: <Building size={19} strokeWidth={2} />,
      children: [
        {
          heading: "Client Organizations",
          route: "/organization/client/organizations",
          title: "Client Organizations",
          icon: <Building size={16} strokeWidth={2} />,
          PermissionKey: "USER_VIEW_ALL",
        },
      ],
    },
  ], []);

  const [sidebaroption, setSideBarOption] = useState(sidebarOptions);
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    if (CORE_FRONTEND_PERMISSIONS_DISABLED) {
      setSideBarOption(sidebarOptions);
      return;
    }

    if (Permission?.length > 0) {
      const filtered = sidebarOptions
        .map((item) => {
          if (item.children) {
            const allowedChildren = item.children.filter((child) =>
              child.PermissionKey
                ? coreHasPermission(Permission, child.PermissionKey)
                : true
            );

            if (allowedChildren.length === 0) return null;

            return { ...item, children: allowedChildren };
          }

          if (item.PermissionKey) {
            return coreHasPermission(Permission, item.PermissionKey)
              ? item
              : null;
          }

          return item;
        })
        .filter(Boolean);

      setSideBarOption(filtered);
    }
  }, [Permission, sidebarOptions]);

  useEffect(() => {
    const next = {};
    sidebaroption.forEach((item) => {
      if (item.children?.some((c) => c.route === location.pathname)) {
        next[item.heading] = true;
      }
    });
    if (Object.keys(next).length) {
      setOpenGroups((prev) => ({ ...prev, ...next }));
    }
  }, [location.pathname, sidebaroption]);

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to logout?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0891B2",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("token");

          if (!isLocalAuthToken(token)) {
            await axios.get(`${MAIN_URL}/api/auth/logout`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
          }

          logout();
          clearLocalAuthSession();

          Swal.fire("Logged out!", "You have been logged out.", "success");
          navigate("/login");
        } catch (error) {
          console.error("Logout failed:", error);
          Swal.fire("Error", "Logout failed. Please try again.", "error");
        }
      }
    });
  };

  const handleNav = (route) => navigate(route);

  const handleParentNavClick = (heading) => {
    if (collapsed) {
      onRequestExpand?.();
      setOpenGroups((o) => ({ ...o, [heading]: true }));
      return;
    }
    setOpenGroups((o) => ({ ...o, [heading]: !o[heading] }));
  };

  const handleLeafNavClick = (route) => {
    if (collapsed) {
      onRequestExpand?.();
    }
    handleNav(route);
  };

  let seenLabels = new Set();

  return (
    <>
      <div className="ems-sidebar-scroll">
        {sidebaroption.map((item, index) => {
          const sectionLabel = SECTION_BEFORE[item.heading];
          const showLabel = sectionLabel && !seenLabels.has(sectionLabel);
          if (showLabel) seenLabels.add(sectionLabel);

          const isActive = !item.children && item.route === location.pathname;
          const childActive =
            item.children &&
            item.children.some((c) => c.route === location.pathname);
          const isOpen = !!openGroups[item.heading];

          return (
            <React.Fragment key={item.heading || index}>
              {showLabel && (
                <div className="ems-nav-section-label">{sectionLabel}</div>
              )}
              {item.children ? (
                <>
                  <button
                    className={[
                      "ems-nav-item",
                      childActive ? "parent-active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => handleParentNavClick(item.heading)}
                    title={collapsed ? item.title : undefined}
                  >
                    <span className="ems-ni-icon">{item.icon}</span>
                    <span className="ems-ni-label">{item.title}</span>
                    <span className={`ems-ni-chev${isOpen ? " open" : ""}`}>
                      <ChevronRight size={16} strokeWidth={2.4} />
                    </span>
                  </button>
                  {!collapsed && (
                    <NavChildren open={isOpen}>
                      {item.children.map((child) => (
                        <button
                          key={child.route}
                          className={[
                            "ems-nav-child",
                            location.pathname === child.route ? "active" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          onClick={() => handleLeafNavClick(child.route)}
                        >
                          <span className="ems-nc-icon">{child.icon}</span>
                          <span className="ems-nc-label">{child.title}</span>
                        </button>
                      ))}
                    </NavChildren>
                  )}
                </>
              ) : (
                <button
                  className={[
                    "ems-nav-item",
                    isActive ? "active" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handleLeafNavClick(item.route)}
                  title={collapsed ? item.title : undefined}
                >
                  <span className="ems-ni-icon">{item.icon}</span>
                  <span className="ems-ni-label">{item.title}</span>
                </button>
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div className="ems-sidebar-foot">
        <button
          className="ems-nav-item"
          onClick={handleLogout}
          title={collapsed ? "Logout" : undefined}
        >
          <span className="ems-ni-icon">
            <LogOut size={19} strokeWidth={2} />
          </span>
          <span className="ems-ni-label">Logout</span>
        </button>
      </div>
    </>
  );
}
